'use client';

import { useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import type { ControllerRenderProps, ControllerFieldState } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Turnstile } from '@marsidev/react-turnstile';
import Rocket from '@icons/Rocket.svg';
import { Elements } from '@/components';
import featuresBackground from '@images/hero-bg.webp';
import Image from 'next/image';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

type ContactFormValues = {
    fullName: string;
    email: string;
    phone: string;
    organization: string;
    message: string;
};

const EMAIL_REGEX = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

export const ContactUs = () => {
    const {
        control,
        handleSubmit,
        reset,
        formState: { isSubmitting },
    } = useForm<ContactFormValues>({
        defaultValues: {
            fullName: '',
            email: '',
            phone: '',
            organization: '',
            message: '',
        },
    });

    const [status, setStatus] = useState<SubmitStatus>('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const turnstileTokenRef = useRef<string | null>(null);
    const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY as string | undefined;
    const router = useRouter();
    const lastSubmitRef = useRef<number>(0);

    const onSubmit = async (values: ContactFormValues) => {
        const now = Date.now();
        if (now - lastSubmitRef.current < 5000) return;
        lastSubmitRef.current = now;

        if (isSubmitting || status === 'submitting') return;

        if (turnstileSiteKey && !turnstileTokenRef.current) {
            setStatus('error');
            setErrorMessage('Please complete the captcha.');
            return;
        }

        setStatus('submitting');
        setErrorMessage('');

        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...values,
                    message: values.message || undefined,
                    turnstileToken: turnstileTokenRef.current || undefined,
                }),
            });
            const raw = await res.text();
            let data: { success?: boolean; error?: string; detail?: string } = {};
            try {
                data = raw ? (JSON.parse(raw) as { success?: boolean; error?: string; detail?: string }) : {};
            } catch {
                const preview = raw.slice(0, 80).replace(/\s+/g, ' ');
                setStatus('error');
                setErrorMessage(
                    res.ok
                        ? 'Invalid response from server.'
                        : `Server error${preview ? `: ${preview}${raw.length > 80 ? '…' : ''}` : '.'}`
                );
                return;
            }

            if (!res.ok || !data.success) {
                setStatus('error');
                const msg = data.error || 'Something went wrong.';
                setErrorMessage(data.detail ? `${msg}: ${data.detail}` : msg);
                return;
            }

            setStatus('success');
            reset();
            turnstileTokenRef.current = null;
            router.push('/thank-you');
        } catch (err) {
            setStatus('error');
            const msg = err instanceof Error && err.message ? err.message : 'Network error. Please try again later.';
            const hint =
                process.env.NODE_ENV !== 'production' ? ' Make sure the API route /api/contact is reachable.' : '';
            setErrorMessage(msg + (hint ? ` ${hint}` : ''));
        }
    };

    return (
        <section
            className="overflow-hidden bg-cover bg-center bg-no-repeat pt-10 pb-10 md:pt-20 md:pb-20"
            style={{ backgroundImage: `url(${featuresBackground})` }}
        >
            <div className="mx-auto flex w-full max-w-[1200px] flex-col items-start justify-between gap-8 px-4 lg:flex-row lg:px-0">
                <div className="flex w-full flex-1 flex-col justify-center justify-items-center gap-4 lg:items-center">
                    <div className="mb-6 flex w-full flex-col items-center justify-center gap-4 lg:items-start">
                        <Elements.Banner
                            variant="gradient"
                            icon={<Image src={Rocket} alt="Rocket" width={20} height={20} />}
                        >
                            Revolutionizing ICHRA Technology
                        </Elements.Banner>
                        <Elements.Title
                            title="The future of Health Benefits is here"
                            type="h1"
                            className="text-center text-gray-50 lg:text-left"
                        />
                        <Elements.Description
                            description="Deliver flexible ICHRA solutions to your clients with full cost control and maximum customization"
                            type="default"
                            className="text-center text-gray-50 lg:text-left"
                        />
                    </div>

                    <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3">
                        <Elements.ContactUsCard
                            title="Fixed budgets"
                            description="Fixed monthly contributions for precise cost planning"
                        />
                        <Elements.ContactUsCard
                            title="Custom plans"
                            description="Tailored packages for different employee categories"
                        />
                        <Elements.ContactUsCard
                            title="24/7 Support"
                            description="Expert assistance at every implementation stage"
                        />
                    </div>
                </div>

                <div className="relative w-full flex-1 lg:max-w-[630px]">
                    <div className="relative flex w-full flex-col gap-4 rounded-[20px] bg-[#0077DF] p-4 md:p-6 lg:p-8">
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                padding: 2,
                                borderRadius: 20,
                                background:
                                    'linear-gradient(108deg, rgba(255, 255, 255, 0.51) 1.5%, rgba(255, 255, 255, 0.09) 100%)',
                                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                WebkitMaskComposite: 'xor',
                                maskComposite: 'exclude',
                                pointerEvents: 'none',
                            }}
                        />

                        <div className="relative flex flex-col gap-2">
                            <Elements.Title title="Let's Talk" type="h3" className="text-left text-gray-50" />
                            <Elements.Description
                                description="While others compete with you, we empower you"
                                type="medium"
                                className="text-left text-gray-50"
                            />
                        </div>

                        <form onSubmit={handleSubmit(onSubmit)} className="relative flex flex-col gap-3">
                            <Controller
                                name="fullName"
                                control={control}
                                rules={{
                                    required: 'Full name is required',
                                    minLength: { value: 2, message: 'Full name must be at least 2 characters' },
                                    maxLength: { value: 100, message: 'Full name cannot exceed 100 characters' },
                                }}
                                render={({
                                    field,
                                    fieldState,
                                }: {
                                    field: ControllerRenderProps<ContactFormValues, 'fullName'>;
                                    fieldState: ControllerFieldState;
                                }) => (
                                    <Elements.Input
                                        id="full-name"
                                        label="Full name"
                                        className="w-full rounded-xl"
                                        crystalBorder
                                        inputWrapStyle={{
                                            border: 'none',
                                            backgroundColor: '#0077DF6B',
                                            borderRadius: 8,
                                        }}
                                        required
                                        labelClassName="!text-[#FFFFFF99]"
                                        inputClassName="text-white placeholder-white/60 text-sm font-normal"
                                        placeholder="John Smith"
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        error={fieldState.error?.message}
                                    />
                                )}
                            />
                            <Controller
                                name="email"
                                control={control}
                                rules={{
                                    required: 'Email is required',
                                    pattern: { value: EMAIL_REGEX, message: 'Enter a valid email address' },
                                }}
                                render={({
                                    field,
                                    fieldState,
                                }: {
                                    field: ControllerRenderProps<ContactFormValues, 'email'>;
                                    fieldState: ControllerFieldState;
                                }) => (
                                    <Elements.Input
                                        id="email"
                                        label="Email"
                                        type="email"
                                        className="w-full rounded-xl"
                                        crystalBorder
                                        inputWrapStyle={{
                                            border: 'none',
                                            backgroundColor: '#0077DF6B',
                                            borderRadius: 8,
                                        }}
                                        required
                                        labelClassName="!text-[#FFFFFF99]"
                                        inputClassName="text-white placeholder-white/60 text-sm font-normal"
                                        placeholder="example@email.com"
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        error={fieldState.error?.message}
                                    />
                                )}
                            />
                            <Controller
                                name="phone"
                                control={control}
                                rules={{
                                    required: 'Phone number is required',
                                    minLength: { value: 7, message: 'Phone number looks too short' },
                                }}
                                render={({
                                    field,
                                    fieldState,
                                }: {
                                    field: ControllerRenderProps<ContactFormValues, 'phone'>;
                                    fieldState: ControllerFieldState;
                                }) => (
                                    <Elements.Input
                                        id="phone"
                                        label="Phone Number"
                                        className="w-full rounded-xl"
                                        crystalBorder
                                        inputWrapStyle={{
                                            border: 'none',
                                            backgroundColor: '#0077DF6B',
                                            borderRadius: 8,
                                        }}
                                        required
                                        labelClassName="!text-[#FFFFFF99]"
                                        inputClassName="text-white placeholder-white/60 text-sm font-normal"
                                        placeholder="+1-888-111-2222"
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        error={fieldState.error?.message}
                                    />
                                )}
                            />
                            <Controller
                                name="organization"
                                control={control}
                                rules={{
                                    required: 'Organization is required',
                                    minLength: { value: 2, message: 'Organization must be at least 2 characters' },
                                }}
                                render={({
                                    field,
                                    fieldState,
                                }: {
                                    field: ControllerRenderProps<ContactFormValues, 'organization'>;
                                    fieldState: ControllerFieldState;
                                }) => (
                                    <Elements.Input
                                        id="organization"
                                        label="Organization"
                                        className="w-full rounded-xl"
                                        crystalBorder
                                        inputWrapStyle={{
                                            border: 'none',
                                            backgroundColor: '#0077DF6B',
                                            borderRadius: 8,
                                        }}
                                        required
                                        labelClassName="!text-[#FFFFFF99]"
                                        inputClassName="text-white placeholder-white/60 text-sm font-normal"
                                        placeholder="Type here"
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        error={fieldState.error?.message}
                                    />
                                )}
                            />
                            <Controller
                                name="message"
                                control={control}
                                rules={{
                                    maxLength: { value: 1000, message: 'Message cannot exceed 1000 characters' },
                                }}
                                render={({
                                    field,
                                    fieldState,
                                }: {
                                    field: ControllerRenderProps<ContactFormValues, 'message'>;
                                    fieldState: ControllerFieldState;
                                }) => (
                                    <Elements.Textarea
                                        id="message"
                                        label="Message"
                                        className="w-full"
                                        crystalBorder
                                        textareaWrapStyle={{
                                            border: 'none',
                                            backgroundColor: '#0077DF6B',
                                            borderRadius: 8,
                                        }}
                                        labelClassName="!text-[#FFFFFF99]"
                                        textareaClassName="text-white placeholder-white/60 text-sm font-normal"
                                        placeholder="Type your message here..."
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        error={fieldState.error?.message}
                                    />
                                )}
                            />

                            {turnstileSiteKey && (
                                <div className="mt-1 flex justify-start">
                                    <Turnstile
                                        siteKey={turnstileSiteKey}
                                        onSuccess={(token: string) => {
                                            turnstileTokenRef.current = token;
                                        }}
                                        onExpire={() => {
                                            turnstileTokenRef.current = null;
                                        }}
                                    />
                                </div>
                            )}

                            {status === 'error' && errorMessage && (
                                <p className="text-sm text-red-200">{errorMessage}</p>
                            )}

                            <div className="relative mt-2 flex w-full justify-center lg:justify-end">
                                <Elements.Button
                                    type="submit"
                                    variant="white"
                                    size="lg"
                                    disabled={status === 'submitting' || isSubmitting}
                                    loading={status === 'submitting' || isSubmitting}
                                >
                                    Submit
                                </Elements.Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};
